import React, { useCallback, useMemo, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
    useApproveCliSessionMutation,
    useCancelCliSessionMutation,
    useCliSessionQuery,
} from "@api/cliSession.api";
import { useWorkspacesList } from "@api/workspace.api";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { Picker } from "@design-system/Picker/Picker.lib";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { MainInterface } from "@components/mainInterface/MainInterface";
import { CliIcon, CopyIcon } from "@icons";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import type { CliSession } from "@common/schemas/domains/cliSession/cli-session.schema";
import { AgentAccessTargetPicker } from "../authorize/AgentAccessTargetPicker";
import { AuthorizeHeader } from "../authorize/AuthorizeHeader";
import { PermissionsToggle } from "../authorize/PermissionsToggle";
import {
    AgentAccessTargetFormValues,
    buildAgentAccessGrantTarget,
    getAgentAccessTargetValidationMessage,
} from "../authorize/agentAccessTarget.helpers";
import styles from "../authorize/AuthorizePage.module.scss";

const EXPIRY_PRESETS: Array<{ label: string; value: number | null }> = [
    { label: "7 days", value: 7 },
    { label: "30 days (recommended)", value: 30 },
    { label: "60 days", value: 60 },
    { label: "90 days", value: 90 },
    { label: "No expiration", value: null },
];

const EXPIRY_SENTINEL = "never" as const;

const encodeExpiry = (days: number | null): string =>
    days === null ? EXPIRY_SENTINEL : String(days);

const decodeExpiry = (value: string | null): number | null => {
    if (value === null || value === EXPIRY_SENTINEL) {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const EXPIRY_ITEMS = EXPIRY_PRESETS.map((preset) => ({
    value: encodeExpiry(preset.value),
    label: preset.label,
}));

const cliAuthorizeFormSchema = z
    .object({
        scope: z.enum(["read", "write"]),
        targetType: z.enum(["all", "organizations", "workspaces"]),
        organizationIds: z.array(z.string().uuid()),
        workspaceIds: z.array(z.string().uuid()),
        expiryDays: z.number().int().nullable(),
        name: z.string().trim().min(1, "Token name is required").max(120),
    })
    .superRefine((values, ctx) => {
        const validationMessage = getAgentAccessTargetValidationMessage(values);
        if (values.targetType === "organizations" && validationMessage) {
            ctx.addIssue({
                path: ["organizationIds"],
                code: z.ZodIssueCode.custom,
                message: validationMessage,
            });
        }
        if (values.targetType === "workspaces" && validationMessage) {
            ctx.addIssue({
                path: ["workspaceIds"],
                code: z.ZodIssueCode.custom,
                message: validationMessage,
            });
        }
    });

type CliAuthorizeFormValues = z.infer<typeof cliAuthorizeFormSchema>;

const fields = createFormikFields<CliAuthorizeFormValues>();

const getInitialValues = (session: CliSession): CliAuthorizeFormValues => ({
    scope: session.requestedScope ?? "write",
    targetType: session.requestedWorkspaceId ? "workspaces" : "all",
    organizationIds: [],
    workspaceIds: session.requestedWorkspaceId
        ? [session.requestedWorkspaceId]
        : [],
    expiryDays: session.requestedExpiresInDays ?? 30,
    name: session.requestedName ?? `Kick CLI — ${session.hostname}`,
});

interface CliAuthorizeFormProps {
    sessionId: string;
}

const CliAuthorizeForm: React.FC<CliAuthorizeFormProps> = ({ sessionId }) => {
    const { data: session, isLoading, isError } = useCliSessionQuery(sessionId);
    const { data: workspaces } = useWorkspacesList({ includeEntities: false });
    const approveMutation = useApproveCliSessionMutation(sessionId);
    const cancelMutation = useCancelCliSessionMutation(sessionId);

    const [terminalState, setTerminalState] = useState<
        "approved" | "cancelled" | null
    >(null);
    const [isCopied, setIsCopied] = useState(false);
    const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const handleCopyFingerprint = useCallback(async (fingerprint: string) => {
        try {
            await navigator.clipboard.writeText(fingerprint);
            if (copyResetTimeoutRef.current) {
                clearTimeout(copyResetTimeoutRef.current);
            }
            setIsCopied(true);
            copyResetTimeoutRef.current = setTimeout(
                () => setIsCopied(false),
                2000,
            );
        } catch {
            setIsCopied(false);
        }
    }, []);

    const initialValues = useMemo<CliAuthorizeFormValues>(
        () =>
            session
                ? getInitialValues(session)
                : {
                      scope: "write",
                      targetType: "all",
                      organizationIds: [],
                      workspaceIds: [],
                      expiryDays: 30,
                      name: "",
                  },
        [session],
    );

    const formik = useFormik<CliAuthorizeFormValues>({
        initialValues,
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(cliAuthorizeFormSchema),
        onSubmit: submitHelper({
            handler: async (values) => {
                await approveMutation.mutateAsync({
                    permissions:
                        values.scope === "read" ? ["read"] : ["read", "write"],
                    grantTarget: buildAgentAccessGrantTarget(values),
                    expiresInDays: values.expiryDays,
                    name: values.name.trim(),
                });
                setTerminalState("approved");
            },
        }),
    });

    const handleCancel = useCallback(async () => {
        await cancelMutation.mutateAsync();
        setTerminalState("cancelled");
    }, [cancelMutation]);

    if (isLoading) {
        return <Loader />;
    }

    if (isError || !session) {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="Request not found"
                        body={
                            <>
                                This CLI authorization request was not found.
                                You can close this tab and retry{" "}
                                <code>kick login</code> in your terminal.
                            </>
                        }
                    />
                </div>
            </div>
        );
    }

    if (terminalState === "approved" || session.status === "approved") {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="You're all set"
                        body="Return to your terminal — the CLI should complete sign-in within a few seconds. You can close this tab."
                    />
                </div>
            </div>
        );
    }

    if (
        terminalState === "cancelled" ||
        session.status === "cancelled" ||
        session.status === "expired"
    ) {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="Authorization cancelled"
                        body={
                            <>
                                No token was issued. You can re-run{" "}
                                <code>kick login</code> to start a new request.
                                It's safe to close this tab.
                            </>
                        }
                    />
                </div>
            </div>
        );
    }

    if (session.status === "consumed") {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="Session already completed"
                        body="This CLI session has already been completed."
                    />
                </div>
            </div>
        );
    }

    const { values, setFieldValue, isSubmitting, isValid } = formik;
    const workspaceOptions = workspaces ?? [];
    const targetValidationMessage =
        getAgentAccessTargetValidationMessage(values);
    const selectedExpiry = EXPIRY_PRESETS.find(
        (preset) => preset.value === values.expiryDays,
    );

    return (
        <FormikProvider value={formik}>
            <Form className={styles.card}>
                <AuthorizeHeader
                    leftIcon={
                        <div className={styles.cliHeaderIcon}>
                            <CliIcon className={styles.cliHeaderGlyph} />
                        </div>
                    }
                    title="Authorize Kick CLI"
                />
                <div
                    className={styles.verifyCard}
                    data-testid="cli-authorize-verify-card"
                >
                    <div className={styles.verifyCardHeader}>
                        <span className={styles.verifyCardLabel}>
                            Verify the code below matches your terminal
                        </span>
                        <button
                            type="button"
                            className={styles.verifyCopyButton}
                            onClick={() =>
                                handleCopyFingerprint(session.fingerprint)
                            }
                            aria-label={isCopied ? "Copied" : "Copy code"}
                        >
                            <CopyIcon />
                        </button>
                    </div>
                    <div
                        className={styles.verifyCode}
                        data-testid="cli-authorize-fingerprint"
                    >
                        {session.fingerprint}
                    </div>
                </div>

                <section className={styles.section}>
                    <h4 className={styles.sectionHeading}>Permissions</h4>
                    <PermissionsToggle
                        value={values.scope}
                        onChange={(scope) => setFieldValue("scope", scope)}
                    />
                </section>

                <AgentAccessTargetPicker
                    values={values}
                    workspaces={workspaceOptions}
                    onChange={(targetValues: AgentAccessTargetFormValues) => {
                        setFieldValue("targetType", targetValues.targetType);
                        setFieldValue(
                            "organizationIds",
                            targetValues.organizationIds,
                        );
                        setFieldValue(
                            "workspaceIds",
                            targetValues.workspaceIds,
                        );
                    }}
                    errorMessage={targetValidationMessage}
                />

                <section className={styles.section}>
                    <h4 className={styles.sectionHeading}>Token details</h4>
                    <fields.Text
                        name="name"
                        type="text"
                        label="Token name"
                        size="xl"
                        className={styles.textField}
                    />

                    <div className={styles.selectField}>
                        <span className={styles.selectLabel}>Expiration</span>
                        <Picker.Select
                            items={EXPIRY_ITEMS}
                            value={
                                selectedExpiry
                                    ? encodeExpiry(selectedExpiry.value)
                                    : null
                            }
                            onValueChange={(value) =>
                                setFieldValue("expiryDays", decodeExpiry(value))
                            }
                            placeholder="Select expiration"
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="xl"
                                    block
                                />
                            }
                        />
                    </div>
                </section>

                <footer className={styles.footer}>
                    <Button
                        variant="secondary"
                        size="xl"
                        type="button"
                        onClick={handleCancel}
                        disabled={cancelMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        variant="primary"
                        size="xl"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isValid || isSubmitting}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Authorize Kick CLI",
                            enableOnFormTags: true,
                            enabled: isValid && !isSubmitting,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        Authorize
                    </ButtonWithLoader>
                </footer>
            </Form>
        </FormikProvider>
    );
};

const CliAuthorizeContent: React.FC<{
    sessionId: string | null;
}> = ({ sessionId }) => {
    if (sessionId) {
        return <CliAuthorizeForm sessionId={sessionId} />;
    }

    return (
        <div className={styles.card}>
            <div className={styles.emptyStateWrap}>
                <EmptyState
                    header="Missing session parameter"
                    body={
                        <>
                            Re-run <code>kick login</code> in your terminal to
                            start a new authorization request.
                        </>
                    }
                />
            </div>
        </div>
    );
};

export const CliAuthorizePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session");

    return (
        <MainInterface sideNavigation={null}>
            <div className={styles.page}>
                <div className={styles.content}>
                    <CliAuthorizeContent sessionId={sessionId} />
                </div>
            </div>
        </MainInterface>
    );
};
